import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import db from './Firebase';
import moment from 'moment';
import {
  Table, Form, Button, Row, Col, Spinner, Pagination
} from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const emptyFilters = {
  regStartDate: '',     // Registered Date range
  regEndDate: '',
  orderStartDate: '',   // Order (delivery) Date range -> LTV isi range ka hoga
  orderEndDate: '',
  customerStage: '',
  hubName: '',
  source: '',
  lastDeliveryDate: ''
};

const CustomerLifecycleReport = () => {
  const [finalData, setFinalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [hubsList, setHubsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 100;

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = finalData.slice(indexOfFirstRow, indexOfLastRow);

  // ---------- Orders (LTV selected order-date range ka) ----------
  const getOrders = async (customer, orderStart, orderEnd) => {
    const q = query(
      collection(db, 'order_history'),
      where('customer_id', '==', customer.customer_id),
      orderBy('delivery_timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => doc.data());

    if (orders.length === 0) {
      return {
        orderCount: 0,
        rangeOrderCount: 0,
        lastDeliveryDate: null,
        lastOrderAmount: 0,
        daysSinceLastOrder: null,
        LTV: 0,
        totalOrderAmount: 0
      };
    }

    const allTimeTotal = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

    // Order date range diya hai to sirf wahi orders, warna sabhi orders
    const rangeOrders = (orderStart && orderEnd)
      ? orders.filter(o => {
          const d = o.delivery_timestamp?.toDate?.();
          return d && d >= orderStart && d <= orderEnd;
        })
      : orders;

    const rangeTotal = rangeOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    const lastOrderDate = orders[0]?.delivery_timestamp?.toDate?.();

    return {
      orderCount: orders.length,
      rangeOrderCount: rangeOrders.length,
      lastDeliveryDate: lastOrderDate || null,
      lastOrderAmount: orders[0]?.total_amount || 0,
      daysSinceLastOrder: lastOrderDate ? moment().diff(moment(lastOrderDate), 'days') : null,
      LTV: Math.round(rangeTotal),          // selected order-date range ka total
      totalOrderAmount: Math.round(allTimeTotal) // ab tak ka poora total
    };
  };

  const getPayments = async (customer) => {
    const q = query(
      collection(db, 'payments'),
      where('customer_id', '==', customer.customer_id),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    const payments = snapshot.docs.map(doc => doc.data());
    const lastRecharge = payments[0];

    return {
      lastRechargeDate: lastRecharge?.timestamp?.toDate?.() || null,
      totalRechargeAmount: payments.reduce((sum, p) => sum + (p.amount || 0), 0)
    };
  };

  const getWalletInfo = async (customerId) => {
    const q = query(
      collection(db, 'wallet_history'),
      where('customer_id', '==', customerId),
      orderBy('created_date', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    const latest = snapshot.docs[0]?.data();
    return { ledger_balance: latest?.current_wallet_balance ?? '0' };
  };

  // Ek hi query se active subs aur customer stage dono
  const getSubscriptionInfo = async (customerId) => {
    const snap = await getDocs(
      query(collection(db, 'subscriptions_data'), where('customer_id', '==', customerId))
    );
    const subs = snap.docs.map(d => d.data());
    const activeCount = subs.filter(sub => sub.status === '1').length;

    let stage = 'Lead';
    if (subs.length > 0) {
      const nonOneTimeExists = subs.some(sub => sub.subscription_type !== 'One Time');
      stage = (subs.length > 1 || nonOneTimeExists) ? 'Subscriber' : 'Customer';
    }
    return { activeSubCount: activeCount > 0 ? activeCount : '-', customer_stage: stage };
  };

  // Delivery boy ka naam cache (same executive baar-baar query na ho)
  const deliveryBoyCache = React.useRef(new Map());
  const getDeliveryBoyName = async (execId) => {
    if (!execId) return 'N/A';
    if (deliveryBoyCache.current.has(execId)) return deliveryBoyCache.current.get(execId);
    let name = 'N/A';
    try {
      const snap = await getDocs(
        query(collection(db, 'hubs_users_data'), where('hub_user_id', '==', execId))
      );
      if (!snap.empty) {
        const data = snap.docs[0].data();
        name = `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'N/A';
      }
    } catch (err) {
      console.error('Error fetching delivery boy:', err);
    }
    deliveryBoyCache.current.set(execId, name);
    return name;
  };

  // Sirf order date range diya ho: us range ke orders se customer_ids nikalo,
  // phir un customers ka data lao (Firestore 'in' limit 30 hai, isliye chunks)
  const getCustomersByOrderRange = async (orderStart, orderEnd) => {
    const ordSnap = await getDocs(
      query(
        collection(db, 'order_history'),
        where('delivery_timestamp', '>=', orderStart),
        where('delivery_timestamp', '<=', orderEnd)
      )
    );
    const ids = [...new Set(ordSnap.docs.map(d => d.data().customer_id).filter(Boolean))];

    const chunks = [];
    for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));

    const results = await Promise.all(
      chunks.map(chunk =>
        getDocs(query(collection(db, 'customers_data'), where('customer_id', 'in', chunk)))
      )
    );
    return results.flatMap(snap => snap.docs);
  };

  // ---------- Main fetch ----------
  const fetchCustomerData = async () => {
    setLoading(true);
    setErrorMsg('');
    setCurrentPage(1);
    try {
      const hasReg = filters.regStartDate && filters.regEndDate;
      const hasOrder = filters.orderStartDate && filters.orderEndDate;

      if (!hasReg && !hasOrder) {
        setFinalData([]);
        setLoading(false);
        return;
      }

      const regStart = hasReg ? moment(filters.regStartDate).startOf('day').toDate() : null;
      const regEnd = hasReg ? moment(filters.regEndDate).endOf('day').toDate() : null;
      const orderStart = hasOrder ? moment(filters.orderStartDate).startOf('day').toDate() : null;
      const orderEnd = hasOrder ? moment(filters.orderEndDate).endOf('day').toDate() : null;

      let customerDocs;
      if (hasReg) {
        const custSnap = await getDocs(
          query(
            collection(db, 'customers_data'),
            where('registered_date', '>=', regStart),
            where('registered_date', '<=', regEnd)
          )
        );
        customerDocs = custSnap.docs;
      } else {
        customerDocs = await getCustomersByOrderRange(orderStart, orderEnd);
      }

      const BATCH_SIZE = 20;
      let failedCount = 0;
      const list = [];
      for (let i = 0; i < customerDocs.length; i += BATCH_SIZE) {
        setProgress(`Loading ${Math.min(i + BATCH_SIZE, customerDocs.length)} / ${customerDocs.length} customers...`);
        const batch = customerDocs.slice(i, i + BATCH_SIZE);
        const batchResult = await Promise.all(
          batch.map(async doc => {
            const customer = doc.data();
            const customerId = customer.customer_id;

            try {
              if (!customerId) throw new Error('customer_id missing');

              const [orderInfo, paymentInfo, walletInfo, subInfo, deliveryBoyName] =
                await Promise.all([
                  getOrders(customer, orderStart, orderEnd),
                  getPayments(customer),
                  getWalletInfo(customerId),
                  getSubscriptionInfo(customerId),
                  getDeliveryBoyName(customer.delivery_exe_id)
                ]);

              return {
                ...customer,
                ...orderInfo,
                ...paymentInfo,
                ...walletInfo,
                ...subInfo,
                delivery_boy_name: deliveryBoyName
              };
            } catch (err) {
              // ek customer fail ho to poori report na ruke
              console.error('Customer failed:', doc.id, customerId, err);
              failedCount += 1;
              return {
                ...customer,
                orderCount: 0,
                rangeOrderCount: 0,
                lastOrderAmount: 0,
                LTV: 0,
                totalOrderAmount: 0,
                totalRechargeAmount: 0,
                ledger_balance: '-',
                activeSubCount: '-',
                customer_stage: '-',
                delivery_boy_name: 'N/A'
              };
            }
          })
        );
        list.push(...batchResult);
      }
      setProgress('');
      if (failedCount > 0) {
        setErrorMsg(`${failedCount} customers ka data load nahi ho paya (details browser console mein). Baaki data neeche dikh raha hai.`);
      }

      const filteredList = list.filter(cust => {
        // Order date range laga hai to us range mein jinka koi order nahi, unhe hata do
        if (hasOrder && cust.rangeOrderCount === 0) return false;

        if (filters.customerStage && cust.customer_stage !== filters.customerStage) return false;
        if (filters.hubName && !cust.hub_name?.toLowerCase().includes(filters.hubName.toLowerCase())) return false;
        if (filters.source && !cust.source?.toLowerCase().includes(filters.source.toLowerCase())) return false;
        if (filters.lastDeliveryDate) {
          const lastDate = moment(filters.lastDeliveryDate).format('YYYY-MM-DD');
          const custDate = cust.lastDeliveryDate
            ? moment(cust.lastDeliveryDate).format('YYYY-MM-DD')
            : '';
          if (custDate !== lastDate) return false;
        }
        return true;
      });

      setFinalData(filteredList);
    } catch (err) {
      console.error('Error fetching report:', err);
      setProgress('');
      setErrorMsg(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const subsSnap = await getDocs(collection(db, 'subscriptions_data'));
        const hubsSet = new Set();
        subsSnap.docs.forEach(doc => {
          const data = doc.data();
          if (data.hub_name) hubsSet.add(data.hub_name);
        });
        setHubsList([...hubsSet].sort());
      } catch (err) {
        console.error('Error fetching dropdown data', err);
      }
    };
    fetchDropdownData();
  }, []);

  const downloadData = (type) => {
    const dataToDownload = finalData.map((cust) => ({
      'Customer ID': cust.customer_id,
      'Name': cust.customer_name,
      'Phone': cust.customer_phone,
      'Address': cust.customer_address,
      'Registered Date': cust.registered_date ? moment(cust.registered_date.toDate?.()).format('DD-MM-YYYY') : '-',
      'Platform': cust.platform || '-',
      'Hub': cust.hub_name || '-',
      'Source': cust.source || '-',
      'Order Count (All Time)': cust.orderCount ?? 0,
      'Order Count (Selected Period)': cust.rangeOrderCount ?? 0,
      'Last Order Amount': cust.lastOrderAmount ?? 0,
      'Last Delivery': cust.lastDeliveryDate ? moment(cust.lastDeliveryDate).format('DD-MM-YYYY') : '-',
      'Days Since Last Order': cust.daysSinceLastOrder ?? '-',
      'Last Recharge': cust.lastRechargeDate ? moment(cust.lastRechargeDate).format('DD-MM-YYYY') : '-',
      'Total Recharge': cust.totalRechargeAmount ?? 0,
      'Customer Stage': cust.customer_stage,
      'Ledger Balance': cust.ledger_balance,
      'Active Subs': cust.activeSubCount ?? '-',
      'LTV (Selected Period)': cust.LTV,
      'Total Order Amount (All Time)': cust.totalOrderAmount,
      'Delivery Boy': cust.delivery_boy_name || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToDownload);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers');

    const fileName = `customer_report_${moment().format('YYYYMMDD_HHmmss')}.${type}`;

    if (type === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, fileName);
    } else {
      XLSX.writeFile(wb, fileName);
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setFinalData([]);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(finalData.length / rowsPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const windowSize = 2; // current page ke dono taraf kitne numbers
    const start = Math.max(1, currentPage - windowSize);
    const end = Math.min(totalPages, currentPage + windowSize);

    const items = [];
    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => paginate(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => paginate(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => paginate(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex flex-column align-items-center mb-4">
        <Pagination className="mb-1 flex-wrap justify-content-center">
          <Pagination.First disabled={currentPage === 1} onClick={() => paginate(1)} />
          <Pagination.Prev disabled={currentPage === 1} onClick={() => paginate(currentPage - 1)} />
          {items}
          <Pagination.Next disabled={currentPage === totalPages} onClick={() => paginate(currentPage + 1)} />
          <Pagination.Last disabled={currentPage === totalPages} onClick={() => paginate(totalPages)} />
        </Pagination>
        <small className="text-muted">
          Showing {indexOfFirstRow + 1}-{Math.min(indexOfLastRow, finalData.length)} of {finalData.length} customers
        </small>
      </div>
    );
  };

  return (
    <div className="container mt-4">
      <h4>Customer Lifecycle Report</h4>

      <Form className="border p-3 rounded shadow-sm mb-4 bg-light">
        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>Registered From</Form.Label>
            <Form.Control type="date" name="regStartDate" value={filters.regStartDate} onChange={handleFilterChange} />
          </Col>
          <Col md={3}>
            <Form.Label>Registered To</Form.Label>
            <Form.Control type="date" name="regEndDate" value={filters.regEndDate} onChange={handleFilterChange} />
          </Col>
          <Col md={3}>
            <Form.Label>Order From (LTV period)</Form.Label>
            <Form.Control type="date" name="orderStartDate" value={filters.orderStartDate} onChange={handleFilterChange} />
          </Col>
          <Col md={3}>
            <Form.Label>Order To (LTV period)</Form.Label>
            <Form.Control type="date" name="orderEndDate" value={filters.orderEndDate} onChange={handleFilterChange} />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>Customer Stage</Form.Label>
            <Form.Control as="select" name="customerStage" value={filters.customerStage} onChange={handleFilterChange}>
              <option value="">All</option>
              <option>Lead</option>
              <option>Customer</option>
              <option>Subscriber</option>
            </Form.Control>
          </Col>
          <Col md={3}>
            <Form.Label>Hub Name</Form.Label>
            <Form.Control as="select" name="hubName" value={filters.hubName} onChange={handleFilterChange}>
              <option value="">All</option>
              {hubsList.map((hub, idx) => <option key={idx}>{hub}</option>)}
            </Form.Control>
          </Col>
          <Col md={3}>
            <Form.Label>Source</Form.Label>
            <Form.Control type="text" name="source" value={filters.source} onChange={handleFilterChange} />
          </Col>
          <Col md={3}>
            <Form.Label>Last Delivery Date</Form.Label>
            <Form.Control type="date" name="lastDeliveryDate" value={filters.lastDeliveryDate} onChange={handleFilterChange} />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col md={6}>
            <Button onClick={fetchCustomerData}>Apply Filters</Button>{' '}
            <Button variant="secondary" onClick={clearFilters}>Clear Filters</Button>
          </Col>
          <Col md={6} className="text-end">
            {finalData.length > 0 && (
              <>
                <Button variant="success" onClick={() => downloadData('xlsx')} className="me-2">
                  Download XLSX
                </Button>
                <Button variant="success" onClick={() => downloadData('csv')}>
                  Download CSV
                </Button>
              </>
            )}
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
          {progress && <div className="mt-2 text-muted">{progress}</div>}
        </div>
      ) : (
        <>
          {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
          {finalData.length === 0 && (
            <div className="text-center text-muted mb-3">
              No data to display. Registered Date ya Order Date range select karke filters apply karein.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <Table striped bordered hover responsive className="table w-100" style={{ width: '120%' }}>
              <thead className="table-dark">
                <tr>
                  <th>Customer ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Registered Date</th>
                  <th>Platform</th>
                  <th>Hub</th>
                  <th>Source</th>
                  <th>Order Count</th>
                  <th>Last Order Amount</th>
                  <th>Last Delivery</th>
                  <th>Days Since Last Order</th>
                  <th>Last Recharge</th>
                  <th>Total Recharge</th>
                  <th>Customer-stage</th>
                  <th>wallet-balance</th>
                  <th>Active Subs</th>
                  <th>LTV (Selected Period)</th>
                  <th>Total Order Amount (All Time)</th>
                  <th>Delivery Boy</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((cust, index) => (
                  <tr key={index}>
                    <td>{cust.customer_id}</td>
                    <td>{cust.customer_name}</td>
                    <td>{cust.customer_phone}</td>
                    <td>{cust.customer_address}</td>
                    <td>{cust.registered_date ? moment(cust.registered_date.toDate?.()).format('DD-MM-YYYY') : '-'}</td>
                    <td>{cust.platform || '-'}</td>
                    <td>{cust.hub_name || '-'}</td>
                    <td>{cust.source || '-'}</td>
                    <td>{cust.orderCount ?? 0}</td>
                    <td>{cust.lastOrderAmount ?? 0}</td>
                    <td>{cust.lastDeliveryDate ? moment(cust.lastDeliveryDate).format('DD-MM-YYYY') : '-'}</td>
                    <td>{cust.daysSinceLastOrder ?? '-'}</td>
                    <td>{cust.lastRechargeDate ? moment(cust.lastRechargeDate).format('DD-MM-YYYY') : '-'}</td>
                    <td>{cust.totalRechargeAmount ?? 0}</td>
                    <td>{cust.customer_stage}</td>
                    <td>{cust.ledger_balance}</td>
                    <td>{cust.activeSubCount ?? '-'}</td>
                    <td>{cust.LTV}</td>
                    <td>{cust.totalOrderAmount}</td>
                    <td>{cust.delivery_boy_name || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default CustomerLifecycleReport;